import {
  createContext,
  memo,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { FlowStatus } from '@/apis/flow'
import { StartNodeTypes, type FieldItem } from '@/features/nodes/start'
import type { FlowOutput } from '../../types'
import { transformDataToValidLogs } from '../../utils'
import type { LogItem } from '@/apis/run'
import type { LoggerTab } from './type'

export interface LoggerModalContextProps {
  flowLog?: LogItem
  flowStartType: 'form' | 'webhook'
  formConfig: FieldItem[]
  mode: 'log' | 'debug' | 'diff-debug'
  output: {
    result: string | null
    errorStepName: string | undefined
    errorMsg: string | undefined
  }
  switchTab: (tab: LoggerTab) => void
  setLogs: (logs: FlowOutput[]) => void
  logs: FlowOutput[]
  expandLogKeys: Array<string>
}

const LoggerModalContext = createContext<LoggerModalContextProps>({
  flowLog: undefined,
  flowStartType: 'form',
  formConfig: [],
  mode: 'log',
  output: {
    result: null,
    errorStepName: undefined,
    errorMsg: undefined,
  },
  logs: [],
  expandLogKeys: ['output'],
  switchTab: () => {},
  setLogs: () => {},
})

interface LoggerModalProviderProps {
  children: React.ReactNode
  flowLog?: LogItem
  switchTab: (tab: LoggerTab) => void
  mode: 'log' | 'debug' | 'diff-debug'
}
function getString(data: any): string {
  try {
    return JSON.stringify(data, null, 2)
  } catch (err) {
    return String(data)
  }
}

function getOutput(flowLog?: LogItem) {
  const message = flowLog?.run_result?.message as any
  return {
    result: flowLog?.run_result?.actionOutputs?.output?.output,
    errorStepName: message?.step_name,
    errorMsg:
      flowLog?.run_status === FlowStatus.SUCCEEDED
        ? flowLog?.run_result?.actionOutputs?.output?.output
        : getString(
            message?.step_name
              ? `运行流程节点在${message?.step_name}出错, 详情请查看日志`
              : message,
          ),
  }
}

export const LoggerModalProvider = memo(
  ({ children, flowLog, mode, switchTab }: LoggerModalProviderProps) => {
    const [logs, setLogs] = useState<FlowOutput[]>([])
    const value = useMemo(() => {
      const errorLog = logs.find(log => log.status === FlowStatus.FAIL)
      return {
        flowLog,
        flowStartType: flowLog?.flow_node_type || StartNodeTypes.Form,
        formConfig: flowLog?.form_config || [],
        mode,
        output: getOutput(flowLog),
        expandLogKeys: [
          errorLog?.type === 'SUB_FLOW' || !errorLog ? 'output' : errorLog.name,
        ],
        logs,
        switchTab,
        setLogs: (logs: FlowOutput[]) => setLogs(logs),
      }
    }, [flowLog, logs])

    useEffect(() => {
      if (flowLog) {
        const logs = transformDataToValidLogs(
          flowLog?.run_result.actionOutputs || {},
          {
            isSingleStepRun: (flowLog?.run_type as string) === 'STEP_RUN',
            taskId: flowLog.task_id,
          },
        )

        setLogs(logs)
      }
    }, [flowLog])

    return (
      <LoggerModalContext.Provider value={value}>
        {children}
      </LoggerModalContext.Provider>
    )
  },
)

export function useLogger() {
  return useContext(LoggerModalContext)
}
